<script setup lang="ts">
import MiiaFwAuthModal from './AuthModal.vue'
import MiiaFwAlertMessageLight from './AlertMessageLight.vue'
import { useLoadingView } from '../composables/useLoadingView'
import { resetPassword } from '../utils/miiaFwCognito'
import { reactive, ref, useTemplateRef } from 'vue'

/**
 * @module components/miiafw/auth/MiiaFwPasswordReset
 * @description 「パスワードリセット」画面の機能
 * @vue-data {Boolean} [myModal = false] モーダル制御。trueのときモーダルを開く
 * @vue-data {String} [userName = 空白] モーダル内に表示させるユーザID
 */
const back = useTemplateRef<HTMLButtonElement>('back')
const alertMessageLight =
  useTemplateRef<InstanceType<typeof MiiaFwAlertMessageLight>>('alertMessageLight')
const userNameInput = useTemplateRef<HTMLInputElement>('userNameInput')

const emit = defineEmits<{
  'close-top': []
  'open-signin': [string]
  'open-password-initialize': [string]
}>()

const { setLoadingView } = useLoadingView()

const myModal = ref(false)
const userName = ref('')
const alertView = reactive<{ [key: string]: { hasError: boolean; alertMessage: string } }>({
  un: { hasError: false, alertMessage: '' }
})

/** エラーメッセージを初期化 */
const resetAlertView = () => {
  Object.keys(alertView).forEach(key => {
    alertView[key].hasError = false
    alertView[key].alertMessage = ''
  })
}

/**
 * ユーザIDが入った状態のモーダルを開く
 * @param argUserName - ユーザIDが定義済みのユーザIDならば引数として指定して、モーダルに表示する
 */
const openModal = (argUserName: string): void => {
  if (argUserName !== undefined && argUserName.length > 0) {
    userName.value = argUserName
  }
  myModal.value = true
}

/**
 * モーダル内のユーザIDとエラー表示を消去した後、モーダルを閉じる
 */
const closeMyModal = (): void => {
  userName.value = ''
  resetAlertView()
  myModal.value = false
}

/**
 * 一番上のモーダルを閉じる。＜MiiaFwAuthModal＞タグを指定することで、特定の操作に対応付けることができる
 */
const closeModal = (): void => {
  closeMyModal()
  emit('close-top')
}

/**
 * モーダル上でTab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e - キーボードイベント
 */
const firstTab = (e: KeyboardEvent): void => {
  e.preventDefault()
  e.stopPropagation()
  back.value?.focus()
}

/**
 * モーダル上でShift+Tab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e - キーボードイベント
 */
const lastTab = (e: KeyboardEvent): void => {
  // Shift + Tabは、Tabの遡りなのでデフォルトイベントのまま
  if (!e.shiftKey) {
    e.preventDefault()
    e.stopPropagation()
    userNameInput.value?.focus()
  }
}

/**
 * 「戻る」を押した際、現在のモーダルを閉じて、「ユーザID」が入った状態のサインインモーダルを開く
 */
const backToSignin = (): void => {
  closeMyModal()
  emit('open-signin', userName.value)
}

/**
 * ユーザIDのエラー表示機能。表示されているエラーを初期化し、各項目に対してバリデーションチェックを行う。問題箇所があったらエラーを表示する。問題が無かったらmodule:modules/miiafw/MiiaFwCognito#resetPasswordへユーザIDを渡し、モーダルを閉じる
 */
const doSubmit = (): void => {
  alertMessageLight.value?.deleteAlertMessage()
  resetAlertView()
  const un = userName.value
  if (un.length > 0) {
    // 処理中表示
    setLoadingView(true)
    // パスワード忘れ処理
    resetPassword(un)
      .then(() => {
        setLoadingView(false) // 必ずコンポーネント消滅前に実行
        closeMyModal()
        emit('open-password-initialize', un)
      })
      .catch(error => {
        alertMessageLight.value?.addAlertMessage(error.message)
        setLoadingView(false) // 必ずコンポーネント消滅前に実行
      })
  } else {
    alertView.un.hasError = true
    alertView.un.alertMessage = 'User IDは必須項目です'
  }
}
defineExpose({
  // data
  myModal,
  userName,
  alertView,

  // methods
  openModal,
  closeMyModal,
  closeModal,
  firstTab,
  lastTab,
  backToSignin,
  doSubmit
})
</script>

<template>
  <!-- @closeを指定すると枠外クリック時にモーダルウィンドウを閉じる。 -->
  <!-- <MyModal @close="closeModal" v-if="modal"> -->
  <MiiaFwAuthModal v-if="myModal" @push-esc="backToSignin" @click-close="backToSignin">
    <!-- default スロットコンテンツ -->
    <h4 style="font-size: 1.1rem; font-weight: 500">パスワードリセット</h4>
    <div style="padding: 30px 10px 30px 10px; margin-bottom: 10px; border: 1px solid #bcbcbc">
      <div
        class="form-group"
        style="text-align: left"
        v-bind:class="{ 'has-error': alertView.un.hasError }"
      >
        <label class="control-label">
          ユーザID
          <span v-if="alertView.un.alertMessage.length > 0" class="ff-alert-msg">
            <font-awesome-icon icon="exclamation-circle" />{{ alertView.un.alertMessage }}
          </span>
        </label>
        <input
          ref="userNameInput"
          v-model="userName"
          class="form-control ff-frm-ctrl"
          tabindex="1"
          autocomplete="off"
          v-focus
          @keydown.shift.tab="firstTab"
        />
      </div>
      <div style="text-align: right">
        <button class="btn ff-btn-primary" tabindex="2" @click="doSubmit">
          &nbsp;リセットメール送信
        </button>
      </div>
    </div>
    <MiiaFwAlertMessageLight ref="alertMessageLight"></MiiaFwAlertMessageLight>
  </MiiaFwAuthModal>
</template>
